/**
 * TrajectoryChart Component
 * WP-RETIRE-12: Projected wealth over time, one line per return-rate scenario.
 *
 * The projection grid answers "am I funded at age X?" cell by cell. This
 * answers "what does the whole path look like, and when does the money run
 * out?" — which is what makes the effect of moving a lever legible.
 *
 * Exactly three scenarios, matching RETURN_RATES. The three-scenario model is
 * a simplicity the reporter explicitly asked to keep, so this deliberately
 * does not introduce a continuous risk control.
 *
 * Uses recharts, already a dependency (package.json).
 */
import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  RETURN_RATES,
  RETURN_RATE_LABELS,
} from '@/utils/retirementCalculations';
import {
  buildTrajectorySeries,
  depletionAge,
  formatNetWorthCents,
  seriesKeyForRate,
} from '@/utils/retirementTrajectory';
import type {
  RetirementPlanningInputs,
  RetirementEvent,
} from '@/types/retirement';

export interface TrajectoryChartProps {
  inputs: RetirementPlanningInputs;
  netWorthCents: number;
  events?: RetirementEvent[];
  birthYear?: number;
  className?: string;
}

/** Distinct, colour-blind-safe hues for the three scenarios. */
const SERIES_COLORS: Record<string, string> = {
  rate_5: '#2563eb', // blue
  rate_4: '#7c3aed', // violet
  rate_3: '#c2410c', // orange
};

export default function TrajectoryChart({
  inputs,
  netWorthCents,
  events,
  birthYear,
  className,
}: TrajectoryChartProps) {
  const points = useMemo(
    () => buildTrajectorySeries(inputs, netWorthCents, events, birthYear),
    [inputs, netWorthCents, events, birthYear]
  );

  const depletions = useMemo(
    () =>
      RETURN_RATES.map((rate) => ({
        rate,
        age: depletionAge(points, rate),
      })),
    [points]
  );

  return (
    <div className={className}>
      {/* h3: the page owns the region <h2> (Explore scenarios). */}
      <h3 className="text-sm font-semibold text-gray-900">
        Projected net worth
      </h3>
      <p className="mt-1 mb-2 text-xs text-gray-500">
        Your net worth at each age under this scenario, in today&apos;s
        dollars. It includes your net worth adjustment and life events.
      </p>
      <div className="h-72 w-full" data-testid="trajectory-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={points}
            margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="age"
              tick={{ fontSize: 12 }}
              label={{ value: 'Age', position: 'insideBottom', offset: -4 }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickFormatter={formatNetWorthCents}
              width={70}
            />
            <Tooltip
              formatter={(
                value: number | undefined,
                name: string | undefined
              ) => [
                formatNetWorthCents(value ?? 0),
                name ?? '',
              ]}
              labelFormatter={(age: number) => `Age ${age}`}
            />
            <Legend />

            {/* Retirement age marks the switch from accumulation to drawdown */}
            <ReferenceLine
              x={inputs.retirementAge}
              stroke="#374151"
              strokeDasharray="4 4"
              label={{
                value: 'Retire',
                position: 'top',
                fontSize: 12,
                fill: '#374151',
              }}
            />

            {/* Zero line: crossing it is the money running out */}
            <ReferenceLine y={0} stroke="#9ca3af" />

            {RETURN_RATES.map((rate) => (
              <Line
                key={rate}
                type="monotone"
                dataKey={seriesKeyForRate(rate)}
                name={RETURN_RATE_LABELS[rate]?.label ?? `${rate * 100}%`}
                stroke={SERIES_COLORS[seriesKeyForRate(rate)] ?? '#2563eb'}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="mt-1 text-xs text-gray-500">
        The dashed line marks your retirement age. A line that reaches $0 stays
        there: the projection does not model borrowing.
      </p>

      {depletions.some((d) => d.age !== null) && (
        <ul className="mt-2 space-y-1">
          {depletions
            .filter((d) => d.age !== null)
            .map((d) => (
              <li key={d.rate} className="text-xs text-gray-600">
                At {RETURN_RATE_LABELS[d.rate]?.label ?? `${d.rate * 100}%`}{' '}
                returns, projected net worth reaches $0 at age{' '}
                <span className="font-medium">{d.age}</span>.
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
